import React, { useEffect, useMemo, useRef, useState } from 'react';
import { prop } from 'ramda';
import { throttle } from 'underscore';

import { ScrollerArrow } from './components/ScrollerArrow';

import { useDeviceSelector } from '@core/js/store/hooks';
import { clsb } from '@core/js/utils/classNames.util';

import {
    DEFAULT_SCROLL_VALUE,
    ELEMENT_TYPES,
    ELEMENTS_GAP,
    INITIAL_CONTAINER_WIDTH,
    INITIAL_ELEMENTS_COUNT
} from '@interlayer/js/app/components/common/Scroller/constants';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { useCheckArrowsAndMaxTransform } from './hooks/useCheckArrowsAndMaxTransform';
import { calculateScroll } from './helpers';
import { TScrollerProps } from './types';

import style from './Scroller.scss';

// @TODO implement test
export const Scroller: React.FC<TScrollerProps> = ({
    children,
    classNames = {},
    gridSize = 1,
    isScroll = true,
    isHiddenShadowArrow,
    isShowInactiveArrows = false
}) => {
    const isTouchDevice = useDeviceSelector(prop('isTouchDevice'));
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const containerEl = containerRef.current;
    const scrollContainerEl = scrollContainerRef.current;
    const [transformAmount, setTransformAmount] = useState(0);
    const [touchContainerOffsetLeft, setTouchContainerOffsetLeft] = useState(0);
    const buildCls = clsb([style, classNames]);
    const [basicElemWidth, setBasicElemWidth] = useState(600);
    const basicElemHeight = useRef(165);
    const [maxWidth, setMaxWidth] = useState(Infinity);
    const gridGap = useRef(ELEMENTS_GAP);
    const offsetLeft = isTouchDevice ? touchContainerOffsetLeft : Math.min(transformAmount, maxWidth);
    const childrenProps = useRef<{ width: number; height: number; left: number }[]>([]);
    const childrenLength = React.Children.count(children);
    const visibleIndexes = useMemo<ELEMENT_TYPES[]>(() => {
        return Array.from({ length: childrenLength }).map((_: any, i: number) => {
            if (i < INITIAL_ELEMENTS_COUNT * gridSize) {
                return ELEMENT_TYPES.element;
            }

            return ELEMENT_TYPES.empty;
        });
    }, [childrenLength, gridSize]);

    const {
        isVisibleLeftArrow,
        isVisibleRightArrow,
        maxTransformAmount
    } = useCheckArrowsAndMaxTransform({
        isScroll,
        containerEl,
        scrollContainerEl,
        transformAmount
    });

    const isShowArrowsByDefault = isShowInactiveArrows && Number(maxTransformAmount) > DEFAULT_SCROLL_VALUE;

    const handlerScroll = (isLeftScroll?: boolean) => {
        if (!containerEl || !scrollContainerEl) {
            return;
        }

        setTransformAmount(calculateScroll(containerEl, scrollContainerEl, Boolean(isLeftScroll)));
    };

    useEffect(() => {
        if (scrollContainerEl?.children) {
            let minElemHeight = 0;
            for (let i = 0; i < childrenLength; i++) {
                const elem = scrollContainerEl.children[i] as HTMLDivElement;
                const width = elem?.offsetWidth || basicElemWidth;

                if (!childrenProps.current[i] && elem) {
                    if (basicElemWidth > elem.offsetWidth && elem.offsetWidth) {
                        setBasicElemWidth(elem.offsetWidth);
                    }

                    if (!minElemHeight || basicElemHeight.current > elem.offsetHeight && elem.offsetHeight) {
                        minElemHeight = elem.offsetHeight;
                    }

                    childrenProps.current[i] = {
                        width: elem.offsetWidth,
                        height: elem.offsetHeight,
                        left: elem.offsetLeft
                    };
                }

                const lastElemIndex = childrenProps.current.length - 1;
                const left = childrenProps.current[i]?.left || childrenProps.current[i]?.left === 0
                    ? childrenProps.current[i]?.left
                    : childrenProps.current[lastElemIndex].left + (i - lastElemIndex) * basicElemWidth;

                if (left < offsetLeft / gridSize - width * 3 * gridSize) {
                    visibleIndexes[i] = ELEMENT_TYPES.dummy;
                } else if (
                    left >= offsetLeft + (containerEl?.offsetWidth || INITIAL_CONTAINER_WIDTH) * gridSize + width * 2 * gridSize
                ) {
                    visibleIndexes[i] = ELEMENT_TYPES.empty;
                } else {
                    visibleIndexes[i] = ELEMENT_TYPES.element;
                }
            }

            if (minElemHeight) {
                basicElemHeight.current = minElemHeight;
            }

            if (childrenProps.current[childrenLength - 1] && containerEl) {
                setMaxWidth(
                    childrenProps.current[childrenLength - 1].left
                    + childrenProps.current[childrenLength - 1].width
                    - containerEl.offsetWidth
                );
            }
        }
    }, [basicElemWidth, childrenLength, containerEl, gridSize, offsetLeft, scrollContainerEl, visibleIndexes]);

    useEffect(() => {
        if (!containerEl || !isTouchDevice) {
            return;
        }

        const handleMobileScroll = throttle(() => {
            setTouchContainerOffsetLeft(containerEl?.scrollLeft || 0);
        }, 100);

        containerEl.addEventListener('scroll', handleMobileScroll);

        return () => {
            containerEl.removeEventListener('scroll', handleMobileScroll);
        };
    }, [containerEl, isTouchDevice]);

    useEffect(() => {
        if (scrollContainerEl) {
            gridGap.current = Number(window.getComputedStyle(scrollContainerEl).gap.replace('px', ''));
        }
    }, [scrollContainerEl]);

    if (!children) {
        return null;
    }

    return (
        <div
            className={
                clsx(
                    buildCls(['wrapper']),
                    { [style.isHiddenShadowArrow]: isHiddenShadowArrow }
                )
            }
        >
            <ScrollerArrow
                isLeft
                isVisible={isVisibleLeftArrow || isShowArrowsByDefault}
                classNames={{
                    ...classNames,
                    ...!isVisibleLeftArrow && { arrowContainer: clsx(style.isInactive, classNames.arrowContainer) }
                }}
                onClick={handlerScroll}
            />

            <div
                ref={containerRef}
                className={
                    clsx(
                        buildCls(['scrollContainer']),
                        { [style.isTouchDevice]: isTouchDevice }
                    )
                }
            >
                <div
                    data-testid="Scroller_container"
                    ref={scrollContainerRef}
                    className={buildCls(['container'])}
                    style={{
                        transform: `translateX(-${Math.min(transformAmount, maxWidth)}px)`,
                        gridTemplateRows: `repeat(${gridSize}, max-content)`
                    }}
                >
                    {visibleIndexes.map((elem, i) => {
                        if (elem === ELEMENT_TYPES.element) {
                            return children[i];
                        }

                        if (elem === ELEMENT_TYPES.dummy) {
                            return (
                                <div
                                    key={`${elem}_${i}`}
                                    style={{
                                        width: childrenProps.current[i]?.width,
                                        height: childrenProps.current[i]?.height,
                                        gridColumnEnd: childrenProps.current[i]?.width / basicElemWidth > 1 ? 'span 2' : '',
                                        gridRowEnd: childrenProps.current[i]?.height / basicElemHeight.current > 1 ? 'span 2' : ''
                                    }}
                                />
                            );
                        }

                        return null;
                    })}
                </div>
            </div>

            <ScrollerArrow
                isVisible={isVisibleRightArrow || isShowArrowsByDefault}
                classNames={{
                    ...classNames,
                    ...!isVisibleRightArrow && { arrowContainer: clsx(style.isInactive, classNames.arrowContainer) }
                }}
                onClick={handlerScroll}
            />
        </div>
    );
};
